import { format, isSameYear } from "date-fns";
import { Doc } from "@repo/convex/convex/_generated/dataModel";
import { parseXPost, youTubeThumbnailUrl, itemKind } from "./itemKind.ts";

// Display helpers, after the iPhone app's Format (StashItKit/Format.swift).

/** "agentdiaries.blog" from a link, without the www. */
export const domain = (url: string) => {
  try {
    const host = new URL(url).hostname;
    return host.startsWith("www.") ? host.slice(4) : host;
  } catch {
    return url;
  }
};

/** The title, or the domain until there is one. A post on X is what it said. */
export const itemTitle = (item: Pick<Doc<"items">, "title" | "url">) =>
  parseXPost(item.title, item.url)?.text ?? item.title ?? domain(item.url);

/** About 230 words a minute, and about six characters of markdown a word. */
export const readingMinutes = (characters: number) =>
  Math.max(1, Math.round(characters / 6 / 230));

export const minutesLeft = (total: number, position: number) =>
  Math.max(0, Math.ceil(total * (1 - Math.min(1, Math.max(0, position)))));

/** "0:45", "41:26", "1:02:03". Null when it isn't known, so there's no badge rather than a made-up one. */
export const videoLength = (seconds: number | null | undefined) => {
  if (!seconds || seconds <= 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor(seconds / 60) % 60;
  const s = Math.floor(seconds % 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

/** "2 Oct", or "2 Oct 2024" if it wasn't this year. */
export const shortDate = (at: number) =>
  format(at, isSameYear(at, Date.now()) ? "d MMM" : "d MMM yyyy");

/** "in 3 days", "tomorrow", "today": when the trash deletes something. */
export const deletesIn = (at: number) => {
  const days = Math.ceil((at - Date.now()) / 86_400_000);
  if (days <= 0) return "today";
  if (days == 1) return "tomorrow";
  return `in ${days} days`;
};

/** A web link from what someone typed or pasted ("example.com/post" gets https://), or null. */
export const normalizedLink = (raw: string) => {
  const t = raw.trim();
  if (!t || t.includes(" ")) return null;
  try {
    const url = new URL(t.includes("://") ? t : "https://" + t);
    if (url.protocol != "http:" && url.protocol != "https:") return null;
    if (!url.hostname.includes(".")) return null;
    return url.toString();
  } catch {
    return null;
  }
};

/** The item's picture, best first: its header image, then a YouTube video's own thumbnail. */
export const pictureUrls = (item: Pick<Doc<"items">, "headerImageUrl" | "url">) => {
  const urls = item.headerImageUrl ? [item.headerImageUrl] : [];
  const kind = itemKind(item.url);
  if (kind.kind == "video" && kind.youTubeId) urls.push(youTubeThumbnailUrl(kind.youTubeId));
  return urls;
};

/**
 * Its site's icon, best first: the one the page names, then `/favicon.ico` on its host. Both come
 * from the site itself, never a favicon service, which would be handed the whole reading list.
 */
export const siteIconUrls = (item: Pick<Doc<"items">, "siteIconUrl" | "url">) => {
  const urls: string[] = [];
  if (item.siteIconUrl) urls.push(item.siteIconUrl.replace(/^http:\/\//, "https://"));
  try {
    const url = new URL(item.url);
    if (url.protocol == "http:" || url.protocol == "https:") {
      const favicon = `https://${url.host}/favicon.ico`;
      if (!urls.includes(favicon)) urls.push(favicon);
    }
  } catch {
    // Not a link we can find an icon for; the letter it is
  }
  return urls;
};

// A site's colour (the iPhone app's Silk), from a small palette by a stable hash of its domain, so
// anything from one site always gets the same wash behind its icon when it has no picture, and the
// same colour for the reader's progress line. `pale` ones need deepening to show as a thin line.
const silks = [
  { day: 0xd39b36, pale: true },
  { day: 0x2f5a46, pale: false },
  { day: 0x2c4060, pale: false },
  { day: 0x5c3550, pale: false },
  { day: 0x23585c, pale: false },
  { day: 0x8e2b25, pale: false },
  { day: 0xd69284, pale: true },
  { day: 0xa3b78b, pale: true },
];

const silkFor = (siteDomain: string) => {
  // FNV-1a, as the app does, so a site's colour matches on both
  let hash = 0x811c9dc5;
  for (const byte of new TextEncoder().encode(siteDomain.toLowerCase()))
    hash = Math.imul(hash ^ byte, 0x01000193) >>> 0;
  return silks[hash % silks.length]!;
};

const hex = (color: number) => "#" + color.toString(16).padStart(6, "0");

const mix = (a: number, b: number, t: number) => {
  const channel = (shift: number) => {
    const x = (a >> shift) & 0xff;
    const y = (b >> shift) & 0xff;
    return Math.round(x + (y - x) * t) << shift;
  };
  return channel(16) | channel(8) | channel(0);
};

export const siteColor = (siteDomain: string) => hex(silkFor(siteDomain).day);

/** The site's colour as a thin line on a reader page: deep enough on paper, light enough on dark. */
export const siteLineColor = (siteDomain: string, onDark: boolean) => {
  const { day, pale } = silkFor(siteDomain);
  if (onDark) return hex(pale ? day : mix(day, 0xffffff, 0.45));
  return hex(pale ? mix(day, 0x000000, 0.12) : day);
};
